import React from "react";

import { cn } from "@/lib/utils";

type Props = {
    length: number;
    min?: number;
    max?: number;
    className?: string;
    /** Narrow `length/max` form for rendering inside an input (vs the full `min / length / max`). */
    compact?: boolean;
};

export const CharCounter: React.FC<Props> = ({ length, min, max, className, compact = false }) => {
    const belowMin = typeof min === "number" && length < min;
    const aboveMax = typeof max === "number" && length > max;

    const hasConstraint = typeof min === "number" || typeof max === "number";

    // OK only when there's at least one constraint, length > 0, not above max,
    // and meets the min if provided. Below-min is not considered a failure (neutral).
    const ok =
        length > 0 &&
        hasConstraint &&
        !aboveMax &&
        (typeof min !== "number" || length >= min);

    const stateClass = length === 0
        ? "text-neutral-text"
        : aboveMax
            ? "text-destructive-text"
            : ok
                ? "text-[var(--success-text)]"
                : "text-neutral-text";

    // display: min / length / max (omit missing parts). Compact mode shows just
    // length/max so it fits unobtrusively inside an input.
    let display = `${length}`;
    if (compact) {
        display = typeof max === "number" ? `${length}/${max}` : `${length}`;
    } else if (typeof min === "number" && typeof max === "number") {
        display = `${min} / ${length} / ${max}`;
    } else if (typeof min === "number") {
        display = `${min} / ${length}`;
    } else if (typeof max === "number") {
        display = `${length} / ${max}`;
    }

    return (
        <div className={cn("w-full text-end text-xs font-normal", stateClass, className)}>
            {display}
        </div>
    );
};

export default CharCounter;
